import type {
  DocumentMetadataUserEditValueKey,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValueKey,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS,
  DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentJSON } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageJSON } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'

/** Prefer an explicit user edit (including null clear) over the generated value. */
export function preferUserEditValue(
  userValue: string | null | undefined,
  generatedValue: string | null | undefined
): string | null | undefined {
  if (userValue !== undefined) {
    return userValue
  }
  return generatedValue
}

export type EffectiveDocumentMetadataValues = {
  [K in DocumentMetadataUserEditValueKey]: string | null | undefined
}

export type EffectivePageMetadataValues = {
  [K in DocumentPageMetadataUserEditValueKey]: string | null | undefined
}

export function mergeDocumentMetadataValues(
  systemData: Partial<SystemDocumentJSON> | undefined,
  editValues: DocumentMetadataUserEditValues | undefined
): EffectiveDocumentMetadataValues {
  const merged: EffectiveDocumentMetadataValues = {
    system: undefined,
    vendor_name: undefined,
    document_type: undefined,
    drawing_number: undefined,
    revision_code: undefined,
    revision_date: undefined,
  }
  for (const key of DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS) {
    const systemValue = systemData?.[key]
    const generated =
      typeof systemValue === 'string' || systemValue === null
        ? systemValue
        : undefined
    merged[key] = preferUserEditValue(
      editValues ? editValues[key] : undefined,
      generated
    )
  }
  return merged
}

export function mergePageMetadataValues(
  systemData: SystemDocumentPageJSON | undefined,
  editValues: DocumentPageMetadataUserEditValues | null | undefined
): EffectivePageMetadataValues {
  const merged: EffectivePageMetadataValues = {
    page_classification: undefined,
    page_classification_decision_method: undefined,
    issuer_drawing_number: undefined,
    secondary_drawing_number: undefined,
    sheet_number: undefined,
    latest_revision: undefined,
    latest_revision_date: undefined,
    system: undefined,
    document_type: undefined,
    document_title: undefined,
    issuer_company_name: undefined,
    issuer_company_role: undefined,
    viewing_rotation: undefined,
    is_revision_controlled_drawing: undefined,
    design_facility: undefined,
  }
  for (const key of DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS) {
    merged[key] = preferUserEditValue(
      editValues ? editValues[key] : undefined,
      systemData?.[key]
    )
  }
  return merged
}

export function applyPageUserEditToSystemData(
  systemData: SystemDocumentPageJSON | undefined,
  editValues: DocumentPageMetadataUserEditValues | null | undefined
): SystemDocumentPageJSON | undefined {
  const hasEditValues = editValues !== undefined && editValues !== null
  if (systemData === undefined && !hasEditValues) {
    return undefined
  }
  if (!hasEditValues) {
    return systemData
  }
  return {
    ...systemData,
    ...mergePageMetadataValues(systemData, editValues),
  }
}
